<template>
  <TableRemote
    v-if="hasPermission('members.view')"
    isSearchable
    isSelectable
    :loading="loading"
    hideDefaultFooter
    :isInfinitScroll="true"
    :headers="tableHeaders"
    :iconHeader="hasPermission('teams.members.invite') && !hasRole('salesRepresentative') ? 'add' : null"
    :items="displayedMembers"
    :pagination="pagination"
    :sortableColumns="['firstname', 'lastname', 'emailinvitation', 'role', 'membersince', 'lastlogin']"
    :title="$t(`roles.${role ? role : 'all'}`)"
    @send-item="redirect"
    @click-icon-header="showModal"
    @resendInvitation="resendInvitation"
    @onSearchInput="onSearch"
    @sendQueryParams="fetchQuery"
    @iconAction="onClickAction"
  >
    <template v-slot:[`item.emailInvitation`]="{ item }">
      <div class="table-cell-email-invitation">
        <span>{{ item.emailInvitation.email }}</span>
        <a
          v-if="item.emailInvitation.invited && hasPermission('teams.members.invite')"
          @click.stop="resendInvitation(item)"
        >
          {{ item.emailInvitation.label }}
        </a>
      </div>
    </template>
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </TableRemote>
  <NoPermissionAlert
    v-else
    :message="$t('permissions.error')"
  />
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import InviteForm from '@/components/invite/TheInviteForm';
import allRoles from '@/constants/roles';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import perpage from '@/constants/pagination';


export default {
    components: {
        TableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            members: [],
            loading: false,
            pagination: { perpage, page: 1 },
            queryParams: { perpage, search: '' },
        };
    },
    computed: {
        ...mapGetters([
            'teamID',
            'hasPermission',
            'hasRole',
            'superAdmin',
        ]),
        tableHeaders() {
            return [
                {
                    text: this.$t('members.labels.firstName'),
                    value: 'firstName',
                },
                {
                    text: this.$t('members.labels.lastName'),
                    value: 'lastName',
                },
                {
                    text: this.$t('members.labels.email'),
                    value: 'emailInvitation',
                    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
                    sort: (a, b) => a.email.localeCompare(b.email),
                },
                {
                    text: this.$t('members.labels.phoneNumber'),
                    value: 'phoneNumber.number',
                },
                {
                    text: this.$t('members.labels.role'),
                    value: 'role',
                },
                {
                    text: this.$t('members.labels.memberSince'),
                    value: 'memberSince',
                },
                {
                    text: this.$t('members.labels.lastLogIn'),
                    value: 'lastLogIn',
                },
            ];
        },
        role() {
            return this.$route.params.role;
        },
        roles() {
            const { FINANCIAL_ADMINISTRATOR, ADMINISTRATOR, ...rolesRest } = allRoles;
            let listRoles = rolesRest;
            if (this.hasPermission('teams.members.invite') && this.hasRole('laboratoryManager')) {
                listRoles = {
                    LABORATORY_MANAGER: allRoles.LABORATORY_MANAGER,
                    LABORATORY_MICROBIOLOGIST: allRoles.LABORATORY_MICROBIOLOGIST,
                    LABORATORY_CHEMIST: allRoles.LABORATORY_CHEMIST,
                    LABORATORY_TECHNICIAN: allRoles.LABORATORY_TECHNICIAN,
                };
            }
            if (this.superAdmin === true) {
                listRoles = {
                    ...listRoles,
                    FINANCIAL_ADMINISTRATOR: allRoles.FINANCIAL_ADMINISTRATOR,
                };
            }
            if (this.hasRole('administrator')) {
                listRoles = {
                    ADMINISTRATOR: allRoles.ADMINISTRATOR,
                    ...listRoles,
                };
            }
            return Object.values(listRoles).map(role => ({
                label: this.$t(`roles.${role.value}`),
                value: role.value,
                overrides: role.overrides,
            }));
        },
        displayedMembers() {
            return this.members.map(({
                id,
                firstName,
                lastName,
                email,
                phoneNumber,
                status,
                memberSince,
                roles,
                lastLogIn,
            }) => ({
                id,
                firstName,
                lastName,
                phoneNumber: typeof (phoneNumber) === 'object' ? phoneNumber : { number: phoneNumber },
                memberSince: this.$options.filters.formatDate(memberSince, 'l'),
                lastLogIn: this.$options.filters.formatDate(lastLogIn, 'l'),
                emailInvitation: {
                    invited: status === 'invited',
                    label: `(${this.$t('forms.labels.resendInvitation')})`,
                    email,
                },
                role: this.$t(`roles.${roles.key}`),
            }));
        },
    },
    watch: {
        role: {
            async handler() {
                await this.fetchQuery({});
            },
            immediate: true,
        },
    },
    methods: {
        onSearch(value) {
            this.queryParams = { search: value, order: '' };
            this.fetchQuery({});
        },
        async getMembers() {
            this.loading = true;
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({ ...this.queryParams }, true);
            const { data } = await this.$rq.getTeamMembers(this.teamID, this.role, cleanQueryParams);
            const clearFinancialAdminUsers = data.members.filter(member => {
                if (this.superAdmin) {
                    return true;
                }
                return member.roles.key !== 'financialAdministrator';
            });
            this.members = [...this.members, ...clearFinancialAdminUsers];
            this.pagination = data.pagination;

            this.loading = false;
        },
        async redirect(member) {
            await this.$router.push({
                name: this.$route.name === 'users' ? 'user' : 'member',
                params: {
                    memberId: member.id,
                },
            });
        },
        showModal() {
            this.$root.$emit('showModal', {
                title: this.$t('teams.addMember'),
                component: Vue.component('InviteForm', InviteForm),
                payload: {
                    roles: this.roles,
                    role: this.role,
                },
                onConfirm: requestPayload => this.addTeamMember(requestPayload),
            });
        },
        async resendInvitation(member) {
            await this.$rq.resendInvitation(this.$store.getters.teamID, {
                memberId: member.id,
            });
            this.$toasted.global.toast_success({
                message: this.$t('msg.invitationResent'),
            });
        },
        async addTeamMember(payload) {
            if (!isEmpty(payload.phoneNumber) && !payload.phoneNumber.valid) {
                this.$toasted.global.toast_error({
                    message: this.$t('profile.phoneError'),
                });
            } else {
                const requestPayload = cloneDeep(payload);

                if (requestPayload.locale === null) {
                    requestPayload.locale = 'en';
                }
                if (typeof requestPayload.roles === 'string' || !Array.isArray(requestPayload.roles)) {
                    requestPayload.roles = requestPayload.roles.split(',');
                }

                await this.$rq.postInviteUser(this.teamID, requestPayload);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.addedMember', {
                        user: `${requestPayload.firstName} ${requestPayload.lastName}`,
                    }),
                });
                this.fetchQuery({});
            }
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.pagination = {};
            this.members = [];
            await this.getMembers();
        },
        onClickAction(icon) {
            if (icon === 'add') {
                this.showModal();
            }
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.loading) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getMembers();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
